import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Loader2, Undo2, CheckCircle2, AlertTriangle, Pencil } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PromotionsQueueDialog = ({ open, onOpenChange, onChanged }) => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState('');
  const [editing, setEditing] = useState({}); // promotion id → draft wording

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/rfp/boilerplate/promotions`, { withCredentials: true });
      setItems(r.data.promotions || []);
    } catch { setItems([]); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const review = async (it, action) => {
    setBusy(it.id);
    try {
      if (action === 'reverted') {
        await axios.post(`${API_URL}/api/rfp/boilerplate/${it.block_id}/restore/${it.id}`, {}, { withCredentials: true });
      }
      const payload = action === 'edited' ? { action, body: editing[it.id] } : { action };
      await axios.post(`${API_URL}/api/rfp/boilerplate/promotions/${it.id}/review`, payload, { withCredentials: true });
      toast.success(action === 'reverted'
        ? `${it.capability_id} reverted to the previous wording`
        : action === 'edited' ? `${it.capability_id} updated with your edited wording (new revision in history)`
          : `${it.capability_id} marked as reviewed`);
      setItems(prev => prev.filter(x => x.id !== it.id));
      onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Action failed'); }
    setBusy('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="lib-promotions-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0F2040]">Promotions to review</DialogTitle>
          <DialogDescription className="text-xs">
            Answers promoted to the library from RFPs by team members. Spot-check each change —
            Looks good keeps it, Edit lets you correct the wording before approving, revert restores the previous wording in one click.
          </DialogDescription>
        </DialogHeader>
        {items === null && <Loader2 className="w-5 h-5 animate-spin text-[#4ABFCF]" />}
        {items?.length === 0 && (
          <p className="text-sm text-slate-400 py-6 text-center" data-testid="lib-promotions-empty">
            Nothing waiting — all promoted changes have been reviewed.
          </p>
        )}
        <div className="space-y-3">
          {(items || []).map(it => (
            <div key={it.id} className="border border-slate-200 rounded-md p-3 space-y-2" data-testid={`lib-promo-item-${it.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold text-[#0F2040]">{it.capability_id}</span>
                <span className="text-xs text-slate-600 truncate">{it.title}</span>
                <span className="flex-1" />
                <span className="text-[10px] text-slate-400">
                  by {it.promoted_by || 'unknown'}{it.project_name ? ` · from "${it.project_name}"` : ''} · {(it.promoted_at || '').slice(0, 10)}
                </span>
              </div>
              {!it.still_current && it.block_exists && (
                <p className="flex items-center gap-1 text-[10px] text-amber-600" data-testid={`lib-promo-changed-${it.id}`}>
                  <AlertTriangle className="w-3 h-3" />The block has been edited again since this promotion — reverting will replace the latest wording too.
                </p>
              )}
              {!it.block_exists && (
                <p className="text-[10px] text-rose-500">The block no longer exists — revert is unavailable.</p>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-[9px] font-semibold uppercase text-slate-400 mb-0.5">Previous wording</p>
                  <p className="text-[11px] text-slate-600 bg-slate-50 border border-slate-100 rounded p-2 max-h-56 overflow-y-auto whitespace-pre-wrap">{it.old_body}</p>
                </div>
                <div>
                  <p className="text-[9px] font-semibold uppercase text-emerald-600 mb-0.5">{editing[it.id] !== undefined ? 'Promoted wording (editing)' : 'Promoted wording'}</p>
                  {editing[it.id] !== undefined
                    ? <Textarea value={editing[it.id]} onChange={e => setEditing(p => ({ ...p, [it.id]: e.target.value }))} className="text-[11px] min-h-[160px]" data-testid={`lib-promo-edit-body-${it.id}`} />
                    : <p className="text-[11px] text-slate-700 bg-emerald-50/50 border border-emerald-100 rounded p-2 max-h-56 overflow-y-auto whitespace-pre-wrap">{it.current_body}</p>}
                </div>
              </div>
              <div className="flex items-center justify-end gap-2">
                {it.block_exists && (
                  <Button size="sm" variant="outline" disabled={busy === it.id}
                    className="h-6 text-[10px] px-2 text-rose-600 border-rose-200 hover:bg-rose-50"
                    onClick={() => review(it, 'reverted')} data-testid={`lib-promo-revert-${it.id}`}>
                    {busy === it.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3 mr-1" />}Revert
                  </Button>
                )}
                {it.block_exists && editing[it.id] === undefined && (
                  <Button size="sm" variant="outline" disabled={busy === it.id} className="h-6 text-[10px] px-2"
                    onClick={() => setEditing(p => ({ ...p, [it.id]: it.current_body }))} data-testid={`lib-promo-edit-${it.id}`}>
                    <Pencil className="w-3 h-3 mr-1" />Edit
                  </Button>
                )}
                {editing[it.id] !== undefined ? (
                  <>
                    <Button size="sm" variant="ghost" disabled={busy === it.id} className="h-6 text-[10px] px-2 text-slate-500"
                      onClick={() => setEditing(p => { const n = { ...p }; delete n[it.id]; return n; })} data-testid={`lib-promo-edit-cancel-${it.id}`}>Cancel</Button>
                    <Button size="sm" disabled={busy === it.id || (editing[it.id] || '').trim().length < 30}
                      className="h-6 text-[10px] px-2 bg-[#0F2040] hover:bg-[#1a3358]"
                      onClick={() => review(it, 'edited')} data-testid={`lib-promo-edit-save-${it.id}`}>
                      {busy === it.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}Save &amp; approve
                    </Button>
                  </>
                ) : (
                  <Button size="sm" disabled={busy === it.id}
                    className="h-6 text-[10px] px-2 bg-[#0F2040] hover:bg-[#1a3358]"
                    onClick={() => review(it, 'approved')} data-testid={`lib-promo-review-${it.id}`}>
                    <CheckCircle2 className="w-3 h-3 mr-1" />Looks good
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
